/* Prototype-only local image deformation. Original picture remains the fallback. */
(() => {
  const script = document.currentScript;
  const art = document.querySelector('.ffp-art');
  if (!art || art.querySelector('canvas')) return;
  const picture = art.querySelector('img'), canvas = document.createElement('canvas');
  const gl = canvas.getContext('webgl', { alpha:false, antialias:false });
  if (!gl) return;
  canvas.setAttribute('aria-hidden','true');
  const vertex = 'attribute vec2 p; varying vec2 uv; void main(){uv=vec2((p.x+1.)*.5,(1.-p.y)*.5);gl_Position=vec4(p,0,1);}';
  const fragment = `precision highp float;
  varying vec2 uv; uniform sampler2D tex, babyPoses, babyPlate; uniform float t, blink, mobile, babyReady; uniform vec2 sourceSize, displaySize;
  vec4 babyFrame(vec2 q,float frame){
    vec2 cell=vec2(mod(frame,3.),floor(frame/3.));
    vec3 rgb=texture2D(babyPoses,(cell+clamp(q,.002,.998))/vec2(3.,2.)).rgb;
    float chroma=max(rgb.r,max(rgb.g,rgb.b))-min(rgb.r,min(rgb.g,rgb.b));
    float a=max(smoothstep(.012,.045,chroma),smoothstep(.82,.92,min(rgb.r,min(rgb.g,rgb.b))));
    return vec4(rgb*a,a);
  }
  vec3 baby(vec3 col,vec2 p){
    vec2 px=p*sourceSize;
    vec2 origin=mobile>.5?vec2(70.,354.):vec2(100.,205.);
    vec2 size=mobile>.5?vec2(255.,235.):vec2(310.,285.);
    vec2 q=(px-origin)/size;
    if(babyReady<.5||q.x<0.||q.x>1.||q.y<0.||q.y>1.)return col;
    vec2 platePx=mobile>.5?(px-vec2(75.,426.))*vec2(300./250.,135./121.)+vec2(100.,298.):px;
    vec2 patch=(platePx-vec2(100.,298.))/vec2(300.,135.);
    float mask=smoothstep(0.,.035,patch.x)*smoothstep(0.,.035,patch.y)*(1.-smoothstep(.965,1.,patch.x))*(1.-smoothstep(.965,1.,patch.y));
    col=mix(col,texture2D(babyPlate,platePx/vec2(1983.,793.)).rgb,mask);
    float cycle=mod(t,23.);
    float progress=cycle<3.?0.:(cycle<6.?smoothstep(3.,6.,cycle):(cycle<20.?1.:1.-smoothstep(20.,23.,cycle)));
    float f=progress*5.;
    vec4 sprite=mix(babyFrame(q,floor(f)),babyFrame(q,min(5.,floor(f)+1.)),fract(f));
    return col*(1.-sprite.a)+sprite.rgb;
  }
  vec3 monitorFeed(vec3 col,vec2 p){
    if(babyReady<.5)return col;
    // Inner LCD corners only; preserve the illustrated bezel and controls.
    vec2 a=mobile>.5?vec2(243.,785.):vec2(722.,453.);
    vec2 b=mobile>.5?vec2(359.,781.):vec2(834.,450.);
    vec2 c=mobile>.5?vec2(247.,851.):vec2(726.,520.);
    vec2 d=mobile>.5?vec2(364.,845.):vec2(839.,516.);
    vec2 px=p*sourceSize;
    vec2 q=vec2(0.5,(px.y-a.y)/(c.y-a.y));
    // Inverse bilinear projection keeps the feed inside the tilted LCD.
    for(int i=0;i<4;i++){
      vec2 left=mix(a,c,q.y),right=mix(b,d,q.y);
      q.x=(px.x-left.x)/(right.x-left.x);
      vec2 top=mix(a,b,q.x),bottom=mix(c,d,q.x);
      q.y=(px.y-top.y)/(bottom.y-top.y);
    }
    if(q.x<0.||q.x>1.||q.y<0.||q.y>1.)return col;
    vec2 origin=mobile>.5?vec2(50.,386.):vec2(70.,245.);
    vec2 size=mobile>.5?vec2(320.,187.):vec2(380.,225.);
    vec2 feedPoint=(origin+q*size)/sourceSize;
    // Same baby() and clock as the crib: no separate animation or drift.
    vec3 feed=baby(texture2D(tex,feedPoint).rgb,feedPoint);
    float edge=min(min(q.x,1.-q.x)*112.,min(q.y,1.-q.y)*66.);
    return mix(col,feed,smoothstep(0.,.8,edge));
  }
  float region(vec2 p,vec2 c,vec2 r){return 1.-smoothstep(.45,1.,length((p-c)/r));}
  vec3 eye(vec3 col,vec2 p,vec2 c,vec2 r){
    vec2 q=(p-c)/r; float front=-1.2+2.4*blink; float mask=(1.-smoothstep(.88,1.,length(q)))*(1.-smoothstep(front-.08,front+.08,q.y));
    col=mix(col,vec3(.365,.245,.59),mask*step(.01,blink));
    float crease=(1.-smoothstep(.055,.12,abs(q.y-(.12+.3*(1.-q.x*q.x)))))*(1.-smoothstep(.72,.88,abs(q.x)))*smoothstep(.75,.98,blink);
    return mix(col,vec3(.20,.11,.34),crease);
  }
  void main(){
    float scale=max(displaySize.x/sourceSize.x,displaySize.y/sourceSize.y);
    vec2 visible=displaySize/(sourceSize*scale);
    vec2 p=uv*visible+(1.-visible)*vec2(.5,.6);
    float m=0.;
    if(mobile>.5){
      m+=region(p,vec2(.515,.21),vec2(.085,.17));m+=region(p,vec2(.795,.31),vec2(.065,.19));
      m+=region(p,vec2(.13,.58),vec2(.085,.065));m+=region(p,vec2(.735,.52),vec2(.08,.07));
      m+=region(p,vec2(.05,.87),vec2(.18,.2));m+=region(p,vec2(.96,.89),vec2(.2,.18));
      m+=region(p,vec2(.95,.3),vec2(.1,.17));
    }else{
      m+=region(p,vec2(.495,.14),vec2(.045,.17));m+=region(p,vec2(.745,.28),vec2(.045,.22));
      m+=region(p,vec2(.33,.52),vec2(.045,.1));m+=region(p,vec2(.7,.51),vec2(.045,.09));
      m+=region(p,vec2(.12,.82),vec2(.14,.17));m+=region(p,vec2(.96,.65),vec2(.08,.2));
      m+=region(p,vec2(.945,.16),vec2(.1,.2));
    }
    float windTime=t*(mobile>.5?.5:.75);
    vec2 wind=vec2(sin(windTime*1.15+p.y*13.)+sin(windTime*.71+p.x*19.)*.3,cos(windTime*.8+p.x*13.)*.25);
    vec2 amplitude=mobile>.5?vec2(.0045,.00175):vec2(.0022,.001);
    vec2 sampleAt=p+wind*min(m,1.)*amplitude;
    vec3 col=monitorFeed(baby(texture2D(tex,sampleAt).rgb,p),p);
    if(mobile>.5){col=eye(col,p,vec2(539./1229.,666./1280.),vec2(19./1229.,23./1280.));col=eye(col,p,vec2(642./1229.,656./1280.),vec2(14./1229.,21./1280.));}
    else{col=eye(col,p,vec2(1006./1983.,354./793.),vec2(17./1983.,21./793.));col=eye(col,p,vec2(1110./1983.,350./793.),vec2(13./1983.,20./793.));}
    gl_FragColor=vec4(col,1.);
  }`;
  function shader(type,code){const s=gl.createShader(type);gl.shaderSource(s,code);gl.compileShader(s);if(!gl.getShaderParameter(s,gl.COMPILE_STATUS))throw Error(gl.getShaderInfoLog(s));return s;}
  let program;
  try {program=gl.createProgram();gl.attachShader(program,shader(gl.VERTEX_SHADER,vertex));gl.attachShader(program,shader(gl.FRAGMENT_SHADER,fragment));gl.linkProgram(program);if(!gl.getProgramParameter(program,gl.LINK_STATUS))throw Error('link');}
  catch(e){return;}
  gl.useProgram(program);const buffer=gl.createBuffer();gl.bindBuffer(gl.ARRAY_BUFFER,buffer);gl.bufferData(gl.ARRAY_BUFFER,new Float32Array([-1,-1,1,-1,-1,1,-1,1,1,-1,1,1]),gl.STATIC_DRAW);
  const pos=gl.getAttribLocation(program,'p');gl.enableVertexAttribArray(pos);gl.vertexAttribPointer(pos,2,gl.FLOAT,false,0,0);
  const uniforms={};['t','blink','mobile','sourceSize','displaySize','babyReady'].forEach(k=>uniforms[k]=gl.getUniformLocation(program,k));
  async function extraTexture(url,unit,name){const img=new Image();img.crossOrigin='anonymous';img.src=url;await img.decode();const tx=gl.createTexture();gl.activeTexture(gl.TEXTURE0+unit);gl.bindTexture(gl.TEXTURE_2D,tx);gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_WRAP_S,gl.CLAMP_TO_EDGE);gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_WRAP_T,gl.CLAMP_TO_EDGE);gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_MIN_FILTER,gl.LINEAR);gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_MAG_FILTER,gl.LINEAR);gl.texImage2D(gl.TEXTURE_2D,0,gl.RGB,gl.RGB,gl.UNSIGNED_BYTE,img);gl.uniform1i(gl.getUniformLocation(program,name),unit);gl.activeTexture(gl.TEXTURE0);}
  if(script?.dataset.babyPoses&&script?.dataset.babyPlate)Promise.all([extraTexture(script.dataset.babyPoses,1,'babyPoses'),extraTexture(script.dataset.babyPlate,2,'babyPlate')]).then(()=>gl.uniform1f(uniforms.babyReady,1)).catch(()=>{});
  const texture=gl.createTexture();gl.bindTexture(gl.TEXTURE_2D,texture);gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_WRAP_S,gl.CLAMP_TO_EDGE);gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_WRAP_T,gl.CLAMP_TO_EDGE);gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_MIN_FILTER,gl.LINEAR);gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_MAG_FILTER,gl.LINEAR);
  const reduced=matchMedia('(prefers-reduced-motion: reduce)');let ready=false,visible=false,raf=0,last=0,time=0,loadedSrc='';
  function draw(now){raf=0;if(!ready||!visible||reduced.matches||document.hidden)return;
    if(last && now-last<32){raf=requestAnimationFrame(draw);return;}
    time+=last?Math.min((now-last)/1000,.05):0;last=now;
    const cycle=(time+4.5)%6.5;
    const b=cycle<.38?Math.sin(Math.PI*cycle/.38):0;
    gl.uniform1f(uniforms.t,time);gl.uniform1f(uniforms.blink,b);gl.drawArrays(gl.TRIANGLES,0,6);canvas.hidden=false;raf=requestAnimationFrame(draw);
  }
  function sync(){cancelAnimationFrame(raf);raf=0;last=0;canvas.hidden=true;if(ready&&!reduced.matches)raf=requestAnimationFrame(draw);}
  async function load(){const src=picture.currentSrc;if(!src||src===loadedSrc)return;loadedSrc=src;const img=new Image();img.crossOrigin='anonymous';img.src=src;try{await img.decode();if(src!==loadedSrc)return;gl.bindTexture(gl.TEXTURE_2D,texture);gl.texImage2D(gl.TEXTURE_2D,0,gl.RGB,gl.RGB,gl.UNSIGNED_BYTE,img);gl.uniform2f(uniforms.sourceSize,img.naturalWidth,img.naturalHeight);gl.uniform1f(uniforms.mobile,img.naturalHeight>img.naturalWidth?1:0);ready=true;resize();}catch(e){canvas.hidden=true;}}
  function resize(){canvas.hidden=true;const r=art.getBoundingClientRect(),d=Math.min(devicePixelRatio,1.5);canvas.width=Math.round(r.width*d);canvas.height=Math.round(r.height*d);gl.viewport(0,0,canvas.width,canvas.height);gl.uniform2f(uniforms.displaySize,r.width,r.height);sync();}
  art.append(canvas);canvas.hidden=true;picture.addEventListener('load',load);new ResizeObserver(()=>{resize();load();}).observe(art);
  new IntersectionObserver(entries=>{visible=entries[0].isIntersecting;sync();},{threshold:.05}).observe(art);
  reduced.addEventListener('change',sync);document.addEventListener('visibilitychange',sync);canvas.addEventListener('webglcontextlost',e=>{e.preventDefault();ready=false;canvas.hidden=true;cancelAnimationFrame(raf);});load();
})();
